import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, UploadCloud, CheckCircle2, AlertTriangle, Headphones, Image as ImageIcon,
  ScanLine, RefreshCw, FileAudio,
} from "lucide-react";

const MEDIA = (p) => `${process.env.REACT_APP_BACKEND_URL}/api/exams/media/${p}`;
const numFromName = (name) => { const m = String(name).match(/(\d+)/); return m ? parseInt(m[1], 10) : null; };

export default function AdminMedia() {
  const [tests, setTests] = useState([]);
  const [slug, setSlug] = useState("");
  const [test, setTest] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [drag, setDrag] = useState(false);
  const [scan, setScan] = useState(null); // {done, total, results:[]}
  const dropRef = useRef(null);
  const perQRef = useRef(null);
  const perQNum = useRef(null);

  useEffect(() => {
    api.get("/exams").then((r) => setTests(r.data.exams || [])).catch(() => toast.error("Could not load tests."));
  }, []);

  const missingSet = new Set((health?.missing || []).map((m) => `${m.number}:${m.kind}`));

  const loadTest = useCallback(async (s) => {
    if (!s) return;
    setLoading(true); setHealth(null); setQuestions([]); setTest(null);
    try {
      const [q, h] = await Promise.all([
        api.get(`/admin/exams/${s}/questions`),
        api.get(`/admin/exams/${s}/media-health`),
      ]);
      setQuestions(q.data.questions || []);
      setTest(q.data.test || null);
      setHealth(h.data);
    } catch { toast.error("Could not load this test."); }
    finally { setLoading(false); }
  }, []);

  const onPick = (s) => { setSlug(s); loadTest(s); };

  const recheck = async () => {
    if (!slug) return;
    try {
      const [q, h] = await Promise.all([
        api.get(`/admin/exams/${slug}/questions`),
        api.get(`/admin/exams/${slug}/media-health`),
      ]);
      setQuestions(q.data.questions || []); setHealth(h.data);
    } catch { toast.error("Re-check failed."); }
  };

  const uploadOne = async (number, file) => {
    const fd = new FormData();
    fd.append("number", number);
    fd.append("file", file);
    await api.post(`/admin/exams/${slug}/audio`, fd);
  };

  const handleFiles = async (fileList) => {
    if (!slug || test?.skill !== "listening") { toast.error("Pick a listening test first."); return; }
    const files = Array.from(fileList);
    const valid = questions.map((q) => q.number);
    const jobs = [];
    for (const f of files) {
      const n = numFromName(f.name);
      if (n == null || !valid.includes(n)) { toast.error(`${f.name}: no matching question number.`); continue; }
      jobs.push({ n, f });
    }
    if (!jobs.length) return;
    setUploading(true);
    let ok = 0;
    for (const j of jobs) {
      try { await uploadOne(j.n, j.f); ok++; }
      catch (e) { toast.error(`Q${j.n}: ${e.response?.data?.detail || "upload failed"}`); }
    }
    setUploading(false);
    if (ok) toast.success(`Uploaded ${ok} audio file(s).`);
    await recheck();
  };

  const onDrop = (e) => { e.preventDefault(); setDrag(false); handleFiles(e.dataTransfer.files); };
  const pickPerQ = (number) => { perQNum.current = number; perQRef.current?.click(); };
  const onPerQChange = (e) => { const f = e.target.files?.[0]; if (f) handleFiles([f]); e.target.value = ""; };

  const scanAll = async () => {
    setScan({ done: 0, total: tests.length, results: [] });
    const results = [];
    for (let i = 0; i < tests.length; i++) {
      const t = tests[i];
      try {
        const { data } = await api.get(`/admin/exams/${t.slug}/media-health`);
        results.push({ slug: t.slug, title: t.title, skill: t.skill, missing: data.missing_count, total: data.total });
      } catch { results.push({ slug: t.slug, title: t.title, skill: t.skill, missing: -1, total: 0 }); }
      setScan({ done: i + 1, total: tests.length, results: [...results] });
    }
    const bad = results.filter((r) => r.missing !== 0).length;
    toast[bad ? "warning" : "success"](bad ? `${bad} test(s) have missing media.` : "All tests have complete media.");
  };

  const listening = tests.filter((t) => t.skill === "listening");
  const reading = tests.filter((t) => t.skill === "reading");

  return (
    <div data-testid="admin-media-page">
      <Link to="/admin" className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"><ArrowLeft className="h-4 w-4" /> Back to Admin</Link>
      <h1 className="font-display text-3xl sm:text-4xl font-semibold mt-3 flex items-center gap-3"><FileAudio className="h-7 w-7 text-primary" /> Exam media manager</h1>
      <p className="text-muted-foreground mt-2">Check that every question's audio &amp; images exist in storage, and drag-and-drop audio to add or replace it — no scripts.</p>

      {/* Global scan */}
      <div className="mt-6 rounded-2xl bg-card border border-border p-5">
        <div className="flex flex-wrap items-center gap-3">
          <button onClick={scanAll} disabled={scan && scan.done < scan.total} data-testid="scan-all-btn"
            className="inline-flex items-center gap-2 px-4 h-10 rounded-full bg-primary text-primary-foreground text-sm font-semibold disabled:opacity-60">
            {scan && scan.done < scan.total ? <Loader2 className="h-4 w-4 animate-spin" /> : <ScanLine className="h-4 w-4" />}
            Scan all {tests.length} tests for missing media
          </button>
          {scan && <span className="text-sm text-muted-foreground">{scan.done}/{scan.total} checked</span>}
        </div>
        {scan && scan.results.length > 0 && (() => {
          const bad = scan.results.filter((r) => r.missing !== 0);
          return (
            <div className="mt-4" data-testid="scan-results">
              {bad.length === 0 && scan.done === scan.total ? (
                <p className="text-sm text-green-700 inline-flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4" /> All scanned tests have complete media.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {bad.map((r) => (
                    <button key={r.slug} onClick={() => onPick(r.slug)} data-testid={`scan-bad-${r.slug}`}
                      className="inline-flex items-center gap-2 px-3 h-9 rounded-full bg-destructive/10 text-destructive border border-destructive/30 text-xs font-semibold hover:bg-destructive/20 transition-colors">
                      <AlertTriangle className="h-3.5 w-3.5" /> {r.title} — {r.missing < 0 ? "check failed" : `${r.missing} missing`}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })()}
      </div>

      {/* Test picker */}
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <label className="text-sm font-semibold">Test:</label>
        <select data-testid="media-test-select" value={slug} onChange={(e) => onPick(e.target.value)}
          className="h-11 rounded-xl border border-border bg-background px-4 text-sm min-w-[240px]">
          <option value="">Select a test…</option>
          <optgroup label="Listening">{listening.map((t) => <option key={t.slug} value={t.slug}>{t.title}</option>)}</optgroup>
          <optgroup label="Reading">{reading.map((t) => <option key={t.slug} value={t.slug}>{t.title}</option>)}</optgroup>
        </select>
        {slug && <button onClick={recheck} data-testid="recheck-btn" className="inline-flex items-center gap-1.5 px-3 h-10 rounded-full bg-secondary text-sm font-semibold hover:bg-accent transition-colors"><RefreshCw className="h-4 w-4" /> Re-check</button>}
        {loading && <Loader2 className="h-5 w-5 animate-spin text-primary" />}
      </div>

      {slug && !loading && test && (
        <>
          {/* Health summary */}
          <div className="mt-5" data-testid="media-health-summary">
            {health?.ok ? (
              <span className="inline-flex items-center gap-2 px-4 h-11 rounded-full bg-green-100 text-green-800 border border-green-300 text-sm font-bold"><CheckCircle2 className="h-4 w-4" /> All {health.total} questions have their media</span>
            ) : (
              <span className="inline-flex items-center gap-2 px-4 h-11 rounded-full bg-destructive/10 text-destructive border border-destructive/30 text-sm font-bold"><AlertTriangle className="h-4 w-4" /> {health?.missing_count} of {health?.total} media file(s) missing</span>
            )}
          </div>

          {/* Drag-and-drop (listening only) */}
          {test.skill === "listening" && (
            <>
              <div ref={dropRef} onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)} onDrop={onDrop}
                data-testid="audio-dropzone"
                className={`mt-5 rounded-2xl border-2 border-dashed p-8 text-center transition-colors ${drag ? "border-primary bg-accent" : "border-border bg-secondary/40"}`}>
                <UploadCloud className={`h-8 w-8 mx-auto ${drag ? "text-primary" : "text-muted-foreground"}`} />
                <p className="mt-2 text-sm font-semibold">Drag &amp; drop audio files here</p>
                <p className="text-xs text-muted-foreground mt-1">Name each file with its question number (e.g. <code>audio_31.mp3</code>). mp3, ogg, wav, m4a · max 25 MB.</p>
                <label className="mt-3 inline-flex items-center gap-2 px-4 h-10 rounded-full bg-primary text-primary-foreground text-sm font-semibold cursor-pointer">
                  <UploadCloud className="h-4 w-4" /> Choose files
                  <input type="file" accept="audio/*" multiple className="hidden" data-testid="audio-file-input" onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} />
                </label>
                {uploading && <p className="mt-3 text-sm text-primary inline-flex items-center gap-1.5"><Loader2 className="h-4 w-4 animate-spin" /> Uploading…</p>}
              </div>
              <input ref={perQRef} type="file" accept="audio/*" className="hidden" onChange={onPerQChange} />
            </>
          )}

          {/* Question list */}
          <div className="mt-6 space-y-3">
            {questions.map((q) => {
              const audioMissing = missingSet.has(`${q.number}:audio`);
              const imageMissing = missingSet.has(`${q.number}:image`);
              const hasAudio = (q.audio_paths || []).length > 0;
              return (
                <div key={q.id} className="rounded-2xl bg-card border border-border p-4" data-testid={`media-q-${q.number}`}>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="h-7 w-7 grid place-items-center rounded-full emerald-surface text-white text-xs font-bold shrink-0">{q.number}</span>
                    <span className="text-sm text-muted-foreground flex-1 min-w-[140px] truncate">{q.question_text}</span>
                    {test.skill === "listening" && (
                      audioMissing
                        ? <span data-testid={`status-${q.number}`} className="inline-flex items-center gap-1 text-xs font-bold text-destructive"><AlertTriangle className="h-3.5 w-3.5" /> Audio missing</span>
                        : <span data-testid={`status-${q.number}`} className="inline-flex items-center gap-1 text-xs font-bold text-green-700"><CheckCircle2 className="h-3.5 w-3.5" /> Audio OK</span>
                    )}
                    {imageMissing && <span className="inline-flex items-center gap-1 text-xs font-bold text-destructive"><ImageIcon className="h-3.5 w-3.5" /> Image missing</span>}
                  </div>
                  {test.skill === "listening" && (
                    <div className="mt-3 flex items-center gap-3 flex-wrap">
                      {hasAudio && !audioMissing && <audio controls src={MEDIA(q.audio_paths[0])} className="h-9 max-w-full" data-testid={`audio-preview-${q.number}`} />}
                      <button onClick={() => pickPerQ(q.number)} disabled={uploading} data-testid={`replace-audio-${q.number}`}
                        className="inline-flex items-center gap-1.5 px-3 h-9 rounded-full bg-secondary text-sm font-semibold hover:bg-accent transition-colors disabled:opacity-60">
                        <UploadCloud className="h-4 w-4" /> {audioMissing ? "Upload audio" : "Replace audio"}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
            {questions.length === 0 && <p className="text-muted-foreground"><Headphones className="h-4 w-4 inline mr-1" /> No questions found.</p>}
          </div>
        </>
      )}
    </div>
  );
}
